"use client"

import { WalletReadyState } from "@solana/wallet-adapter-base"
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react"
import {
  type ChainId,
  walletIconForConnector,
} from "@src/config/walletRegistry"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import { useStellarWallet } from "@src/providers/StellarWalletProvider"
import { useTronWallet } from "@src/providers/TronWalletProvider"
import {
  type LastConnectedWallet,
  useLastConnectedWalletStore,
} from "@src/stores/useLastConnectedWalletStore"
import { useTonWallet } from "@tonconnect/ui-react"
import { useMemo } from "react"
import { useConnectors } from "wagmi"
import type { ChainIcon } from "../DefuseSDK/constants/blockchains"

export type ResolvedLastWallet = {
  record: LastConnectedWallet
  name: string
  icon: ChainIcon | string | undefined
}

// Mirror of the Solana modal's SOLANA_EXTENSION_FIRST_ADAPTERS — adapters
// whose `Loadable` readyState only means "we ship a web-SDK install prompt"
// rather than "actually usable without an extension". Solflare is the
// canonical case; the modal demotes it to not-installed for the same reason
// the Recent tile must hide it.
const SOLANA_EXTENSION_FIRST_ADAPTERS: ReadonlySet<string> = new Set([
  "Solflare",
])

function getRegistryIcon({
  chain,
  walletId,
  name,
}: LastConnectedWallet & { name?: string }): ChainIcon | undefined {
  if (chain === "ton" || chain === "tron" || chain === "webauthn") {
    return undefined
  }
  return walletIconForConnector({ chain: chain as ChainId, id: walletId, name })
}

/**
 * Looks up the persisted "last connected wallet" against the live connector
 * lists and returns enough to render a "Recent" tile on the login page.
 *
 * Returns null when:
 *   - no record is persisted yet, or
 *   - the wallet's chain manifest hasn't loaded (NEAR/Stellar cold start —
 *     the effect kicks off the load; on the next render the tile resolves), or
 *   - the wallet is no longer usable (extension uninstalled, adapter is
 *     `NotDetected`, Stellar `isAvailable: false`, etc.).
 *
 * The "hide when unavailable" rule is the product spec for this tile —
 * showing a greyed-out Recent reads as broken; better to fall through to
 * the regular chain-container picker.
 */
export function useResolvedLastWallet(): ResolvedLastWallet | null {
  const lastConnected = useLastConnectedWalletStore(
    (s) => s.lastConnectedWallet
  )
  const wagmiConnectors = useConnectors()
  const solanaWallet = useSolanaWallet()
  const { connector: nearConnector } = useNearWallet()
  const { availableWallets: stellarWallets } = useStellarWallet()
  const tonWallet = useTonWallet()
  const tronWallet = useTronWallet()

  return useMemo<ResolvedLastWallet | null>(() => {
    if (!lastConnected) return null

    switch (lastConnected.chain) {
      case "evm": {
        const connector = wagmiConnectors.find(
          (c) => c.id === lastConnected.walletId
        )
        if (!connector) return null
        // EIP-6963 only announces installed wallets; always-available
        // connectors (WalletConnect, Coinbase Wallet SDK) are usable
        // without an extension. Anything else without an entry here would
        // have been "not-installed" — the lookup wouldn't yield a connector.
        return {
          record: lastConnected,
          name: connector.name,
          icon:
            getRegistryIcon({ ...lastConnected, name: connector.name }) ??
            connector.icon?.trim() ??
            undefined,
        }
      }

      case "solana": {
        const adapter = solanaWallet.wallets.find(
          (w) => w.adapter.name === lastConnected.walletId
        )
        if (!adapter) return null
        const ready = adapter.readyState
        // Hide unless installed or genuinely loadable.
        if (
          ready !== WalletReadyState.Installed &&
          ready !== WalletReadyState.Loadable
        ) {
          return null
        }
        // Loadable adapters whose only "loadable" path is a web-SDK install
        // prompt (Solflare) are demoted to not-installed by the modal —
        // mirror that here so we don't surface a Recent tile that opens
        // an iframe nagging the user to install.
        if (
          ready === WalletReadyState.Loadable &&
          SOLANA_EXTENSION_FIRST_ADAPTERS.has(adapter.adapter.name)
        ) {
          return null
        }
        return {
          record: lastConnected,
          name: adapter.adapter.name,
          icon:
            getRegistryIcon({ ...lastConnected, name: adapter.adapter.name }) ??
            adapter.adapter.icon ??
            undefined,
        }
      }

      case "near": {
        const wallets = nearConnector?.availableWallets
        if (!wallets) return null // manifest not yet loaded
        const wallet = wallets.find(
          (w) => w.manifest.id === lastConnected.walletId
        )
        if (!wallet) return null
        return {
          record: lastConnected,
          name: wallet.manifest.name,
          icon:
            getRegistryIcon({ ...lastConnected, name: wallet.manifest.name }) ??
            wallet.manifest.icon ??
            undefined,
        }
      }

      case "stellar": {
        const wallet = stellarWallets.find(
          (w) => w.id === lastConnected.walletId
        )
        if (!wallet) return null
        if (!wallet.isAvailable) return null
        return {
          record: lastConnected,
          name: wallet.name,
          icon:
            getRegistryIcon({ ...lastConnected, name: wallet.name }) ??
            wallet.icon ??
            undefined,
        }
      }

      case "ton": {
        if (!tonWallet) return null
        return {
          record: lastConnected,
          name: "TON",
          icon: {
            dark: "/static/icons/network/ton.svg",
            light: "/static/icons/network/ton.svg",
          },
        }
      }

      case "tron": {
        if (!tronWallet.publicKey) return null
        return {
          record: lastConnected,
          name: "Tron",
          icon: {
            dark: "/static/icons/network/tron.svg",
            light: "/static/icons/network/tron.svg",
          },
        }
      }

      case "webauthn": {
        return {
          record: lastConnected,
          name: "Passkey",
          icon: undefined,
        }
      }
    }
  }, [
    lastConnected,
    wagmiConnectors,
    solanaWallet.wallets,
    nearConnector,
    stellarWallets,
    tonWallet,
    tronWallet.publicKey,
  ])
}
